import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { LessonBlocks } from "@/components/LessonBlocks";
import { ConsultantChat } from "@/components/ConsultantChat";
import { DiplomaCard, LESSON_ICONS } from "@/components/School";
import ProductCard from "@/components/ProductCard";
import { FactPills } from "@/components/FactPills";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, MessageCircleHeart, CheckCircle2, GraduationCap } from "lucide-react";

const optionClass = (result, chosen, j) => {
  if (result && j === result.right) return "bg-[#EAF0EC] border-[#4E7363]";
  if (result) return chosen ? "bg-[#FBEDEB] border-[#C87D75]" : "border-[#EAE3DC]";
  return chosen ? "border-[#2D2825] bg-[#FAF8F5]" : "border-[#EAE3DC] hover:border-[#E5A8A0]";
};

const Quiz = ({ lesson, pid, onPassed }) => {
  const [ans, setAns] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/school/${pid}/lesson/${lesson.id}/quiz`, { answers: lesson.quiz.map((_, i) => ans[i]) });
      setRes(data);
      if (data.passed) onPassed(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Non sono riuscita a controllare le risposte, riprova.");
    } finally { setBusy(false); }
  };
  const complete = lesson.quiz.every((_, i) => ans[i] !== undefined);
  return (
    <div className="rounded-3xl bg-white border border-[#EAE3DC] p-5 sm:p-8" data-testid="lesson-quiz">
      <div className="flex items-center gap-2 text-[#C87D75] text-xs uppercase tracking-[0.2em] font-semibold"><GraduationCap size={15} /> Verifica cosa hai imparato</div>
      <div className="mt-4 space-y-6">
        {lesson.quiz.map((q, i) => (
          <div key={q.q}>
            <div className="font-medium text-[#2D2825]">{i + 1}. {q.q}</div>
            <div className="mt-2 grid gap-2">
              {q.options.map((o, j) => {
                const cls = optionClass(res?.results?.[i], ans[i] === j, j);
                return (
                  <button key={o} disabled={!!res} onClick={() => setAns((a) => ({ ...a, [i]: j }))} data-testid={`quiz-${i}-${j}`}
                    className={`text-left rounded-xl border px-4 min-h-[48px] text-sm transition-colors ${cls}`}>{o}</button>
                );
              })}
            </div>
            {res?.results?.[i] && <p className="mt-1.5 text-sm text-[#5C534E]">{res.results[i].correct ? "Giusto! " : "Non proprio: "}{res.results[i].explain}</p>}
          </div>
        ))}
      </div>
      {!res ? (
        <button onClick={submit} disabled={!complete || busy} data-testid="quiz-submit"
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white px-8 min-h-[48px] text-sm disabled:opacity-40 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Controlla le risposte
        </button>
      ) : (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="quiz-result">
          <div className={`font-serif text-2xl ${res.passed ? "text-[#4E7363]" : "text-[#C87D75]"}`}>
            {res.score}/{res.total} · {res.passed ? "Lezione superata!" : res.score >= res.total - 2 ? "Ci sei quasi" : "Rileggi la lezione e riprova"}
          </div>
          {!res.passed && <button onClick={() => { setRes(null); setAns({}); }} data-testid="quiz-retry"
            className="rounded-full border border-[#D2C5B9] px-5 min-h-[44px] text-sm">Riprova</button>}
        </div>
      )}
    </div>
  );
};

export default function Lesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { profile, loading, addToWishlist, inWishlist } = useProfile();
  const [d, setD] = useState(null);
  const [chat, setChat] = useState(false);
  const [diploma, setDiploma] = useState(null);

  useEffect(() => {
    if (!loading && !profile) navigate("/");
    if (profile) { setD(null); api.get(`/school/${profile.id}/lesson/${lessonId}`).then(({ data }) => setD(data)).catch(() => navigate("/")); }
    window.scrollTo(0, 0);
  }, [profile, loading, lessonId, navigate]);

  if (!d) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="animate-spin text-[#C87D75]" /></div>;
  const L = d.lesson; const Icon = LESSON_ICONS[L.icon] || GraduationCap;

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-48" data-testid="lesson-page">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#EAE3DC]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/#scuola" data-testid="lesson-back" className="inline-flex items-center gap-1.5 text-sm text-[#5C534E] min-h-[44px]"><ArrowLeft size={16} /> La Scuola</Link>
          <span className="text-xs text-[#8C7F78]">Lezione {L.n} di {d.total}</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 pt-8 space-y-8">
        <div>
          <div className="w-14 h-14 rounded-2xl bg-[#F9ECE9] text-[#C87D75] flex items-center justify-center"><Icon size={26} /></div>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-[#2D2825] leading-tight" data-testid="lesson-title">{L.title}</h1>
          <p className="mt-2 text-[#5C534E]">{L.subtitle} · {L.minutes} minuti</p>
          {d.done && <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#EAF0EC] text-[#4E7363] text-xs px-3 py-1"><CheckCircle2 size={13} /> Già completata</span>}
        </div>
        <FactPills topic={L.id} />
        <LessonBlocks blocks={L.blocks} data={d.data} />
        {d.products.length > 0 && (
          <div data-testid="lesson-products">
            <h3 className="font-serif text-2xl text-[#2D2825] mb-1">Dalla teoria alla pratica</h3>
            <p className="text-sm text-[#8C7F78] mb-4">I capi Minime adatti al tuo bimbo, scelti in base a questa lezione.</p>
            <div className="mb-rail sm:grid sm:grid-cols-3 gap-3">
              {d.products.map((p) => <ProductCard key={p.id} product={p} onAdd={addToWishlist} inList={inWishlist(p.id)} testid={`lesson-product-${p.id}`} />)}
            </div>
          </div>
        )}
        <Quiz lesson={L} pid={profile.id} onPassed={(r) => r.diploma && setDiploma(r.diploma)} />
        {diploma && <DiplomaCard diploma={diploma} name={profile.name} />}
        {d.next && (
          <Link to={`/scuola/${d.next.id}`} data-testid="lesson-next"
            className="flex items-center justify-between rounded-2xl bg-[#2D2825] text-white px-5 py-4 hover:bg-[#473f3a] transition-colors">
            <span><span className="block text-xs text-white/60 uppercase tracking-widest">Prossima lezione</span>{d.next.title}</span><ArrowRight />
          </Link>
        )}
        <Link to="/#corredini-nascita" data-testid="lesson-birth-sets"
          className="flex items-center justify-between rounded-2xl bg-[#F6F9F7] border border-[#DCE7E0] text-[#2D2825] px-5 py-4 hover:bg-[#EAF0EC] transition-colors">
          <span>
            <span className="block text-xs text-[#4E7363] uppercase tracking-widest">I nostri corredini nascita</span>
            Completi di tutto, in 100% cotone turco certificato o lana merino con tutina in cotone
          </span>
          <ArrowRight className="shrink-0 ml-3" />
        </Link>
      </main>
      <button onClick={() => setChat(true)} data-testid="lesson-ask"
        className="fixed bottom-5 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[#C87D75] hover:bg-[#B56B63] text-white px-5 min-h-[52px] shadow-xl text-sm transition-colors">
        <MessageCircleHeart size={18} /> Chiedi alla consulente
      </button>
      <ConsultantChat open={chat} onOpenChange={setChat} lessonId={L.id} />
    </div>
  );
}
